import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SocketsService } from 'src/app/views/sockets.service';

@Injectable({
  providedIn: 'root',
})
export class MeetingTranscriptionService {
  private socketService = inject(SocketsService);
  private translate = inject(TranslateService);

  isGeminiLiveActive = signal<boolean>(false);

  // Unified Audio Pipeline Properties (16kHz Raw PCM)
  #meetingAudioContext: AudioContext | null = null;
  #workletNode: any = null;
  #micSource: any = null;
  #remoteSource: any = null;
  #mixedSource: any = null;
  #localMicStream: MediaStream | null = null;

  #onMeetingContextReady = (data: any) => {
    console.log('[MeetingTranscriptionService] [meeting-context-ready] Received conversation context:', data.context);
  };

  #onGeminiLiveTextResponse = (data: any) => {
    console.log('[MeetingTranscriptionService] [gemini-live-text-response] Received text answer from Gemini Live:', data.text);
  };

  // ==========================================
  // FEATURE A: Context Builder (Original)
  // ==========================================

  async start(
    callID: string,
    meetingAudioElement: HTMLAudioElement | null,
    selectedAudioInputId: string | null,
    geminiInteraction: string,
    getAudioConstraintsFn: (deviceId: string) => MediaTrackConstraints
  ): Promise<void> {
    try {
      console.log('[MeetingTranscriptionService] [PCM-Pipeline] Starting PCM capture for call:', callID);
      await this.socketService.initSocket();

      if (this.socketService.onEvent) {
        this.socketService.onEvent('meeting-context-ready', this.#onMeetingContextReady);
      }

      if (!meetingAudioElement) {
        console.warn('[MeetingTranscriptionService] [PCM-Pipeline] No meeting audio element found, cannot capture remote audio.');
      }

      // 1. Capture local microphone stream
      let localStream: MediaStream | null = null;
      if (selectedAudioInputId) {
        try {
          const constraints = getAudioConstraintsFn(selectedAudioInputId);
          localStream = await navigator.mediaDevices.getUserMedia({ audio: constraints });
          this.#localMicStream = localStream;
        } catch (e) {
          console.error('[MeetingTranscriptionService] [PCM-Pipeline] Failed to get local microphone stream:', e);
        }
      }

      // 2. Setup AudioContext at 16000Hz to handle automatic resampling
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        console.error('[MeetingTranscriptionService] [PCM-Pipeline] Web Audio API not supported.');
        return;
      }

      const audioCtx = new AudioContextClass({ sampleRate: 16000 });
      this.#meetingAudioContext = audioCtx;

      const dest = audioCtx.createMediaStreamDestination();

      if (meetingAudioElement) {
        try {
          const remoteSource = audioCtx.createMediaElementSource(meetingAudioElement);
          this.#remoteSource = remoteSource;
          remoteSource.connect(dest);
          remoteSource.connect(audioCtx.destination);
        } catch (err) {
          console.error('[MeetingTranscriptionService] [PCM-Pipeline] Error binding remote element source:', err);
        }
      }

      if (localStream && localStream.getAudioTracks().length > 0) {
        const localSource = audioCtx.createMediaStreamSource(localStream);
        this.#micSource = localSource;
        localSource.connect(dest);
      }

      if (audioCtx.state === 'suspended') {
        await audioCtx.resume();
      }

      // 3. Create AudioWorkletNode to process float samples and output 16-bit signed PCM
      const workletCode = `
        class DictationProcessor extends AudioWorkletProcessor {
          process(inputs, outputs, parameters) {
            const input = inputs[0];
            if (input && input.length > 0) {
              const float32Data = input[0];
              const int16Buffer = new Int16Array(float32Data.length);
              for (let i = 0; i < float32Data.length; i++) {
                const s = Math.max(-1, Math.min(1, float32Data[i]));
                int16Buffer[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
              }
              this.port.postMessage(int16Buffer.buffer, [int16Buffer.buffer]);
            }
            return true;
          }
        }
        registerProcessor('dictation-processor', DictationProcessor);
      `;
      const blob = new Blob([workletCode], { type: 'application/javascript' });
      const workletUrl = URL.createObjectURL(blob);
      
      await audioCtx.audioWorklet.addModule(workletUrl);
      const workletNode = new AudioWorkletNode(audioCtx, 'dictation-processor');
      this.#workletNode = workletNode;

      const mixedSource = audioCtx.createMediaStreamSource(dest.stream);
      this.#mixedSource = mixedSource;
      mixedSource.connect(workletNode);
      workletNode.connect(audioCtx.destination);

      workletNode.port.onmessage = (event: MessageEvent) => {
        const rawBuffer = event.data;

        // 1. Send chunk to standard Context Builder
        this.socketService.emitEvent('transcribe-meeting-data', {
          data: rawBuffer,
          meetingId: callID,
          language: this.translate.currentLang || 'fr',
          geminiInteraction: geminiInteraction,
        });

        // 2. Route chunk to Gemini Live if active
        if (this.isGeminiLiveActive()) {
          this.socketService.emitEvent('transcribe-gemini-live-data', {
            data: rawBuffer,
            meetingId: callID,
          });
        }
      };

      console.log('[MeetingTranscriptionService] [PCM-Pipeline] Capture pipeline started successfully.');
    } catch (error) {
      console.error('[MeetingTranscriptionService] [PCM-Pipeline] Unhandled error starting PCM capture:', error);
    }
  }

  stop(): void {
    console.log('[MeetingTranscriptionService] [PCM-Pipeline] Stopping transcription pipeline');
    
    // Stop Gemini Live just in case it was active
    this.isGeminiLiveActive.set(false);

    if (this.#workletNode) {
      try {
        this.#workletNode.port.onmessage = null;
        this.#workletNode.disconnect();
      } catch (e) {
        console.error('[MeetingTranscriptionService] [PCM-Pipeline] Error disconnecting workletNode:', e);
      }
      this.#workletNode = null;
    }

    if (this.#micSource) {
      try {
        this.#micSource.disconnect();
      } catch (e) {
        console.error('[MeetingTranscriptionService] [PCM-Pipeline] Error disconnecting micSource:', e);
      }
      this.#micSource = null;
    }

    if (this.#remoteSource) {
      try {
        this.#remoteSource.disconnect();
      } catch (e) {
        console.error('[MeetingTranscriptionService] [PCM-Pipeline] Error disconnecting remoteSource:', e);
      }
      this.#remoteSource = null;
    }

    if (this.#mixedSource) {
      try {
        this.#mixedSource.disconnect();
      } catch (e) {
        console.error('[MeetingTranscriptionService] [PCM-Pipeline] Error disconnecting mixedSource:', e);
      }
      this.#mixedSource = null;
    }

    if (this.#localMicStream) {
      try {
        this.#localMicStream.getTracks().forEach((track) => track.stop());
      } catch (e) {
        console.error('[MeetingTranscriptionService] [PCM-Pipeline] Error stopping microphone tracks:', e);
      }
      this.#localMicStream = null;
    }

    if (this.#meetingAudioContext) {
      try {
        this.#meetingAudioContext.close();
      } catch (e) {
        console.error('[MeetingTranscriptionService] [PCM-Pipeline] Error closing AudioContext:', e);
      }
      this.#meetingAudioContext = null;
    }

    try {
      this.socketService.emitEvent('meeting-stream-end', {});
      this.socketService.emitEvent('gemini-live-stream-end', {});
    } catch (e) {
      console.error('[MeetingTranscriptionService] [PCM-Pipeline] Error emitting stream-end events:', e);
    }

    if (this.socketService.offEvent) {
      try {
        this.socketService.offEvent('meeting-context-ready', this.#onMeetingContextReady);
        this.socketService.offEvent('gemini-live-text-response', this.#onGeminiLiveTextResponse);
      } catch (e) {
        console.error('[MeetingTranscriptionService] [PCM-Pipeline] Error unsubscribing:', e);
      }
    }
  }

  // ==========================================
  // FEATURE B: Gemini Multimodal Live API
  // ==========================================

  async startGeminiLive(callID: string): Promise<void> {
    try {
      console.log('[MeetingTranscriptionService] [GeminiLive] Activating live assistant for call:', callID);
      await this.socketService.initSocket();

      if (this.socketService.onEvent) {
        this.socketService.onEvent('gemini-live-text-response', this.#onGeminiLiveTextResponse);
      }

      // Turn on chunk routing
      this.isGeminiLiveActive.set(true);
      console.log('[MeetingTranscriptionService] [GeminiLive] Active chunk routing enabled.');
    } catch (error) {
      console.error('[MeetingTranscriptionService] [GeminiLive] Error starting Gemini Live:', error);
    }
  }

  stopGeminiLive(): void {
    console.log('[MeetingTranscriptionService] [GeminiLive] Deactivating live assistant');
    
    // Turn off chunk routing
    this.isGeminiLiveActive.set(false);

    try {
      this.socketService.emitEvent('gemini-live-stream-end', {});
    } catch (e) {
      console.error('[MeetingTranscriptionService] [GeminiLive] Error emitting gemini-live-stream-end:', e);
    }

    if (this.socketService.offEvent) {
      try {
        this.socketService.offEvent('gemini-live-text-response', this.#onGeminiLiveTextResponse);
      } catch (e) {
        console.error('[MeetingTranscriptionService] [GeminiLive] Error unsubscribing:', e);
      }
    }
  }
}
